import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { IntegratedAppSlice, IntegratedAppState, setIntegratedState } from "state/slices/integrated-app-slice";
import { getInitializeGamePath, getPrefixPath } from "utils/backend-path-builders";
import useQueryParams, { queryParamsToObject } from "utils/useQueryParams";
import { GROUP_SLUG } from "utils/constants";
import * as storage from "utils/local-storage";
import { getGroupState, setUserGroupMembershipStatus } from "state/slices/group-slice";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserGroupSettingsByGroupId, UserGroupSettings } from "state/slices/user-settings-slice";
import { addGroupChangeRequestMessageListener } from "utils/integration/iframe-messages";
import { INTEGRATED_APP_HOME_PAGE_URL_PREFIX } from "utils/integration/constants";
import { setUserId } from "../state/slices/user-slice";

const InitializeIntegrationApp = (groupSlug: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const group = useAppSelector(getGroupState);
  const userGroupSettings = useAppSelector(state => getUserGroupSettingsByGroupId(state, group.id));
  const location = useLocation();

  const groupSettingsRef: React.MutableRefObject<{
    userGroupSettings: UserGroupSettings | undefined
  }> = useRef({ userGroupSettings });

  useEffect(() => {
    groupSettingsRef.current = { userGroupSettings };
  }, [userGroupSettings]);

  // Extract groupSlug from either pattern
  const pathParts = location.pathname.split("/");
  const effectiveGroupSlug = groupSlug || pathParts[pathParts.indexOf(INTEGRATED_APP_HOME_PAGE_URL_PREFIX) + 1];

  if (effectiveGroupSlug) {
    storage.local.setItem(GROUP_SLUG, effectiveGroupSlug);
  }

  // Check if we're in an integration app context (either pattern)
  const isIntegratedAppHomePage = location.pathname.includes(INTEGRATED_APP_HOME_PAGE_URL_PREFIX);

  const takeUserToTheIntegratedAppInitialPageIfNeeded = () => {
    const initialPage = queryParams.get("initialPage");
    if (initialPage) {
      navigate(initialPage);
    }
  };

  const sendInitializeGameRequest = async (externalLoginToken: string, externalUserId: string | null) => {
    const queryParamsJson = queryParamsToObject(queryParams);

    const res = await fetch(
      process.env.REACT_APP_BACKEND_URL +
      getPrefixPath() + "/" +
      effectiveGroupSlug +
      getInitializeGamePath(),
      {
        method: "POST",
        headers: {
          "x-user-id": externalUserId || "",
          "x-login-token": externalLoginToken,
        },
        body: JSON.stringify(queryParamsJson),
      }
    );

    const initializeGameResponse = await res.json();

    dispatch(
      setIntegratedState({
        state: IntegratedAppState.INTEGRATED_SESSION_INITIALIZED,
        isIntegratedApp: true,
        userId: initializeGameResponse.external_user_id,
        token: initializeGameResponse.token,
        refreshToken: initializeGameResponse.refresh_token
      } as IntegratedAppSlice)
    );
    dispatch(setUserGroupMembershipStatus(UserGroupMembershipStatus.APPROVED));
    dispatch(setUserId(initializeGameResponse.user_id));
  };

  useEffect(() => {
    if (!isIntegratedAppHomePage) {
      return;
    }

    // Handle both parameter patterns
    const externalUserId = queryParams.get("user_id") || queryParams.get("playerId");
    const externalLoginToken = queryParams.get("login_token") || queryParams.get("loginToken");

    if (externalLoginToken) {
      dispatch(setIntegratedState({
        state: IntegratedAppState.INTEGRATED_SESSION_INITIALIZING,
        isIntegratedApp: true
      } as IntegratedAppSlice));

      sendInitializeGameRequest(externalLoginToken, externalUserId)
        .then(() => takeUserToTheIntegratedAppInitialPageIfNeeded())
        .catch(e => {
          toast.error(`Something went wrong.`);
          console.error("failed to initialize a game", e);
        });
    } else {
      dispatch(setIntegratedState({
        state: IntegratedAppState.INTEGRATED_ANONYMOUS,
        isIntegratedApp: true
      } as IntegratedAppSlice));
      takeUserToTheIntegratedAppInitialPageIfNeeded();
    }
  }, [dispatch, queryParams, isIntegratedAppHomePage]);

  useEffect(() => {
    if (!isIntegratedAppHomePage) return;
    addGroupChangeRequestMessageListener(groupSettingsRef);
  }, [isIntegratedAppHomePage]);
};

export default InitializeIntegrationApp;
